"use client";

import { c as _c } from "react/compiler-runtime";
import { useGameLocale } from "@/lib/i18n";
import { Fragment, type ReactNode } from "react";
import { getCardProseFacts } from "@/lib/card-display";
import { randomPatternSentences, randomPatternSummary } from "@/lib/attack-pattern";
import type { Relic, Potion, Power, Monster, Card, Enchantment, Character, Orb, GameEvent, Encounter, Keyword, Intent, Modifier, Affliction, Achievement, Act, Ascension } from "@/lib/api/types";

/**
 * Programmatic prose block at the bottom of each entity's Overview
 * tab. The prose is English-only, every locale used to render the
 * SAME English boilerplate text on top of localized chrome, which
 * Google's algorithm reads as duplicate content across translations
 * and dumps the localized variants into "Crawled - currently not
 * indexed" (~7,000 pages affected).
 *
 * Behavior now:
 * - English: full prose (60-100 words of factual contextual content
 *   to push the page past Google's "thin content" floor)
 * - Non-English: a single sentence built ENTIRELY from already-
 *   localized API fields (name, rarity, pool, translated server-
 *   side per language). No English connective text. This ensures
 *   each locale's page body is genuinely different from the others
 *   while still adding minimal SEO weight beyond the bare description.
 *
 * Long-term, full localized prose templates would be ideal, but
 * those require professional translation of ~30 sentence patterns
 * × 14 languages. Until then, this asymmetry preserves indexation.
 *
 * Three discriminated variants below; each entity detail page picks
 * the one that matches its data shape.
 */

interface RelicProseProps {
  kind: "relic";
  relic: Relic;
}
interface PotionProseProps {
  kind: "potion";
  potion: Potion;
}
interface PowerProseProps {
  kind: "power";
  power: Power;
  appliedByCount: number;
}
interface MonsterProseProps {
  kind: "monster";
  monster: Monster;
  deadliest?: {
    name: string;
    killRate: number;
  } | null;
}
interface CardProseProps {
  kind: "card";
  card: Card;
  upgraded?: boolean;
}
interface EnchantmentProseProps {
  kind: "enchantment";
  enchantment: Enchantment;
}
interface CharacterProseProps {
  kind: "character";
  character: Character;
}
interface OrbProseProps {
  kind: "orb";
  orb: Orb;
}
interface EventProseProps {
  kind: "event";
  event: GameEvent;
}
interface EncounterProseProps {
  kind: "encounter";
  encounter: Encounter;
}
interface KeywordProseProps {
  kind: "keyword";
  keyword: Keyword;
}
interface IntentProseProps {
  kind: "intent";
  intent: Intent;
}
interface ModifierProseProps {
  kind: "modifier";
  modifier: Modifier;
}
interface AfflictionProseProps {
  kind: "affliction";
  affliction: Affliction;
}
interface AchievementProseProps {
  kind: "achievement";
  achievement: Achievement;
}
interface ActProseProps {
  kind: "act";
  act: Act;
}
interface AscensionProseProps {
  kind: "ascension";
  ascension: Ascension;
}
type Props = (RelicProseProps | PotionProseProps | PowerProseProps | MonsterProseProps | CardProseProps | EnchantmentProseProps | CharacterProseProps | OrbProseProps | EventProseProps | EncounterProseProps | KeywordProseProps | IntentProseProps | ModifierProseProps | AfflictionProseProps | AchievementProseProps | ActProseProps | AscensionProseProps) & {
  // When true the prose renders as a hero lead (no top rule) instead of the
  // default bottom block. Entity detail pages pass this to put the overview
  // right under the H1; relics/potions/powers omit it (bottom of overview tab).
  lead?: boolean;
};

// "a"/"an" by leading vowel sound (good enough for our vocabulary).
function article(word: string): string {
  return /^[aeiou]/i.test(word) ? "an" : "a";
}

// Title-case a raw power id ("INCREASING_INTENSITY" -> "Increasing Intensity")
// for SSR-safe prose (power display names load client-side only).
function titleCaseId(id: string): string {
  return id.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

// "a", "a and b", "a, b, and c"
function listWords(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}
function listNodes(items: ReactNode[]): ReactNode {
  if (items.length <= 1) return items[0] ?? "";
  return items.map((item, i) => <Fragment key={i}>
      {i > 0 && (i === items.length - 1 ? items.length === 2 ? " and " : ", and " : ", ")}
      {item}
    </Fragment>);
}
export default function EntityProse(props) {
  const $ = _c(219);
  const lang = useGameLocale();
  const isEnglish = lang === "eng";
  if (props.kind === "relic") {
    const r = props.relic;
    const name = r.name;
    const rarity = r.rarity;
    const pool = r.pool || "shared";
    if (!isEnglish) {
      const t0 = `${name} · ${rarity} · ${pool}`;
      let t1;
      if ($[0] !== t0) {
        t1 = [t0];
        $[0] = t0;
        $[1] = t1;
      } else {
        t1 = $[1];
      }
      let t2;
      if ($[2] !== props.lead || $[3] !== t1) {
        t2 = <Prose lead={props.lead} sentences={t1} />;
        $[2] = props.lead;
        $[3] = t1;
        $[4] = t2;
      } else {
        t2 = $[4];
      }
      return t2;
    }
    let sentences;
    if ($[5] !== name || $[6] !== pool || $[7] !== r.merchant_price || $[8] !== rarity) {
      sentences = [];
      sentences.push(`${name} is a ${rarity} in the ${pool} relic pool.`);
      if (r.merchant_price?.min && r.merchant_price?.max) {
        sentences.push(`It can be purchased from the merchant for ${r.merchant_price.min}–${r.merchant_price.max} gold (typical range; exact prices use the standard ±15% banker's-rounded variance).`);
      } else {
        sentences.push("It is not sold by the merchant, the only routes to acquire it are reward drops, events, or boss rewards depending on its pool.");
      }
      sentences.push(`Like every relic in Slay the Spire 2, ${name} is preserved across combats unless removed by an event.`);
      $[5] = name;
      $[6] = pool;
      $[7] = r.merchant_price;
      $[8] = rarity;
      $[9] = sentences;
    } else {
      sentences = $[9];
    }
    let t0;
    if ($[10] !== props.lead || $[11] !== sentences) {
      t0 = <Prose lead={props.lead} sentences={sentences} />;
      $[10] = props.lead;
      $[11] = sentences;
      $[12] = t0;
    } else {
      t0 = $[12];
    }
    return t0;
  }
  if (props.kind === "potion") {
    const p = props.potion;
    const name_0 = p.name;
    const rarity_0 = p.rarity;
    const pool_0 = (p as Potion & {
      pool?: string | null;
    }).pool;
    if (!isEnglish) {
      const t0 = `${name_0} · ${rarity_0}${pool_0 ? ` · ${pool_0}` : ""}`;
      let t1;
      if ($[13] !== t0) {
        t1 = [t0];
        $[13] = t0;
        $[14] = t1;
      } else {
        t1 = $[14];
      }
      let t2;
      if ($[15] !== props.lead || $[16] !== t1) {
        t2 = <Prose lead={props.lead} sentences={t1} />;
        $[15] = props.lead;
        $[16] = t1;
        $[17] = t2;
      } else {
        t2 = $[17];
      }
      return t2;
    }
    let sentences_0;
    if ($[18] !== name_0 || $[19] !== pool_0 || $[20] !== rarity_0) {
      sentences_0 = [];
      sentences_0.push(`${name_0} is a ${rarity_0} potion${pool_0 ? ` in the ${pool_0} pool` : ""}.`);
      sentences_0.push("Common potions cost roughly 48\u201353 gold at the merchant, Uncommon 71\u201379 gold, and Rare 95\u2013105 gold (per-rarity variance \xB15%). Potions can also drop from combat rewards based on the per-fight potion drop chance (about 40% base, trending toward 50%, with a +25% bonus in elite fights and a \xB110% pity adjustment after each fight).");
      sentences_0.push(`${name_0} can be saved between combats and used at any point during your turn. Effects trigger immediately and the potion is consumed.`);
      $[18] = name_0;
      $[19] = pool_0;
      $[20] = rarity_0;
      $[21] = sentences_0;
    } else {
      sentences_0 = $[21];
    }
    let t0;
    if ($[22] !== props.lead || $[23] !== sentences_0) {
      t0 = <Prose lead={props.lead} sentences={sentences_0} />;
      $[22] = props.lead;
      $[23] = sentences_0;
      $[24] = t0;
    } else {
      t0 = $[24];
    }
    return t0;
  }
  if (props.kind === "card") {
    const c = props.card;
    const name_1 = c.name;
    let t0;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {
        ironclad: "Ironclad",
        silent: "Silent",
        defect: "Defect",
        necrobinder: "Necrobinder",
        regent: "Regent"
      };
      $[25] = t0;
    } else {
      t0 = $[25];
    }
    const pools = t0;
    if (!isEnglish) {
      const t1 = `${name_1} · ${c.rarity} · ${c.type}`;
      let t2;
      if ($[26] !== t1) {
        t2 = [t1];
        $[26] = t1;
        $[27] = t2;
      } else {
        t2 = $[27];
      }
      let t3;
      if ($[28] !== props.lead || $[29] !== t2) {
        t3 = <Prose lead={props.lead} sentences={t2} />;
        $[28] = props.lead;
        $[29] = t2;
        $[30] = t3;
      } else {
        t3 = $[30];
      }
      return t3;
    }
    const who = pools[c.color] ? ` for the ${pools[c.color]}` : "";
    let sentences_1;
    if ($[31] !== c || $[32] !== props.upgraded || $[33] !== who) {
      const f = getCardProseFacts(c, !!props.upgraded);
      const green = (v, changed) => f.isUpgraded && changed ? <span className="text-success">{v}</span> : v;
      sentences_1 = [];
      sentences_1.push(<>{green(f.displayName, true)} is a {c.rarity} {c.type} card{who} in Slay the Spire 2, costing {green(f.cost, f.costChanged)} energy.</>);
      const eff = [];
      if (f.damage != null) {
        eff.push(<>deals {green(f.damage, f.damageChanged)} damage{f.hitCount && f.hitCount > 1 ? <> across {green(f.hitCount, f.hitCountChanged)} hits</> : f.xTimes ? " X times" : ""}</>);
      }
      if (f.block != null) {
        eff.push(<>grants {green(f.block, f.blockChanged)} Block</>);
      }
      if (c.cards_draw != null) {
        eff.push(`draws ${c.cards_draw} card${c.cards_draw === 1 ? "" : "s"}`);
      }
      if (c.energy_gain != null) {
        eff.push(`gains ${c.energy_gain} energy`);
      }
      const powerParts = (c.powers_applied || []).map(_temp);
      if (powerParts.length) {
        eff.push(`applies ${listWords(powerParts)}`);
      }
      if (eff.length) {
        sentences_1.push(<>It {listNodes(eff)}.</>);
      }
      if (f.keywords.length) {
        let t1;
        if ($[35] !== green) {
          t1 = t2 => {
            const {
              keyword,
              added
            } = t2;
            return green(keyword, added);
          };
          $[35] = green;
          $[36] = t1;
        } else {
          t1 = $[36];
        }
        sentences_1.push(<>It carries the{" "}{listNodes(f.keywords.map(t1))}{" "}keyword{f.keywords.length > 1 ? "s" : ""}.</>);
      }
      $[31] = c;
      $[32] = props.upgraded;
      $[33] = who;
      $[34] = sentences_1;
    } else {
      sentences_1 = $[34];
    }
    let t1;
    if ($[37] !== props.lead || $[38] !== sentences_1) {
      t1 = <Prose lead={props.lead} sentences={sentences_1} />;
      $[37] = props.lead;
      $[38] = sentences_1;
      $[39] = t1;
    } else {
      t1 = $[39];
    }
    return t1;
  }
  if (props.kind === "enchantment") {
    const e = props.enchantment;
    const name_2 = e.name;
    if (!isEnglish) {
      const t0 = `${name_2}${e.card_type ? ` · ${e.card_type}` : ""}`;
      let t1;
      if ($[40] !== t0) {
        t1 = [t0];
        $[40] = t0;
        $[41] = t1;
      } else {
        t1 = $[41];
      }
      let t2;
      if ($[42] !== props.lead || $[43] !== t1) {
        t2 = <Prose lead={props.lead} sentences={t1} />;
        $[42] = props.lead;
        $[43] = t1;
        $[44] = t2;
      } else {
        t2 = $[44];
      }
      return t2;
    }
    let sentences_2;
    if ($[45] !== e.card_type || $[46] !== e.is_stackable || $[47] !== name_2) {
      sentences_2 = [];
      let t0;
      if ($[49] !== e.card_type) {
        t0 = e.card_type ? `, applied to ${e.card_type.toLowerCase()} cards` : "";
        $[49] = e.card_type;
        $[50] = t0;
      } else {
        t0 = $[50];
      }
      sentences_2.push(`${name_2} is a card enchantment in Slay the Spire 2${t0}.`);
      sentences_2.push(e.is_stackable ? `${name_2} stacks, so a single card can hold more than one for a compounding effect.` : `${name_2} does not stack; a card can carry it only once.`);
      $[45] = e.card_type;
      $[46] = e.is_stackable;
      $[47] = name_2;
      $[48] = sentences_2;
    } else {
      sentences_2 = $[48];
    }
    let t0;
    if ($[51] !== props.lead || $[52] !== sentences_2) {
      t0 = <Prose lead={props.lead} sentences={sentences_2} />;
      $[51] = props.lead;
      $[52] = sentences_2;
      $[53] = t0;
    } else {
      t0 = $[53];
    }
    return t0;
  }
  if (props.kind === "character") {
    const ch = props.character;
    const name_3 = ch.name;
    if (!isEnglish) {
      const t0 = `${name_3}${ch.starting_hp != null ? ` · ${ch.starting_hp} HP` : ""}`;
      let t1;
      if ($[54] !== t0) {
        t1 = [t0];
        $[54] = t0;
        $[55] = t1;
      } else {
        t1 = $[55];
      }
      let t2;
      if ($[56] !== props.lead || $[57] !== t1) {
        t2 = <Prose lead={props.lead} sentences={t1} />;
        $[56] = props.lead;
        $[57] = t1;
        $[58] = t2;
      } else {
        t2 = $[58];
      }
      return t2;
    }
    let sentences_3;
    if ($[59] !== ch.max_energy || $[60] !== ch.orb_slots || $[61] !== ch.starting_deck?.length || $[62] !== ch.starting_gold || $[63] !== ch.starting_hp || $[64] !== ch.starting_relics?.length || $[65] !== ch.unlocks_after || $[66] !== name_3) {
      sentences_3 = [];
      const stats = [];
      if (ch.starting_hp != null) {
        stats.push(`${ch.starting_hp} HP`);
      }
      if (ch.starting_gold != null) {
        stats.push(`${ch.starting_gold} gold`);
      }
      if (ch.max_energy != null) {
        stats.push(`${ch.max_energy} energy per turn`);
      }
      sentences_3.push(`${name_3} is a playable character in Slay the Spire 2${stats.length ? `, starting each run with ${listWords(stats)}` : ""}.`);
      const deck = ch.starting_deck?.length || 0;
      const relics = ch.starting_relics?.length || 0;
      if (deck || relics) {
        const parts = [];
        if (deck) {
          parts.push(`a ${deck}-card starting deck`);
        }
        if (relics) {
          parts.push(`${relics} starting relic${relics === 1 ? "" : "s"}`);
        }
        sentences_3.push(`They begin with ${listWords(parts)}.`);
      }
      if (ch.orb_slots) {
        sentences_3.push(`${name_3} channels orbs, with ${ch.orb_slots} orb slot${ch.orb_slots === 1 ? "" : "s"} to start.`);
      }
      if (ch.unlocks_after) {
        sentences_3.push(`${name_3} unlocks after ${ch.unlocks_after}.`);
      }
      $[59] = ch.max_energy;
      $[60] = ch.orb_slots;
      $[61] = ch.starting_deck?.length;
      $[62] = ch.starting_gold;
      $[63] = ch.starting_hp;
      $[64] = ch.starting_relics?.length;
      $[65] = ch.unlocks_after;
      $[66] = name_3;
      $[67] = sentences_3;
    } else {
      sentences_3 = $[67];
    }
    let t0;
    if ($[68] !== props.lead || $[69] !== sentences_3) {
      t0 = <Prose lead={props.lead} sentences={sentences_3} />;
      $[68] = props.lead;
      $[69] = sentences_3;
      $[70] = t0;
    } else {
      t0 = $[70];
    }
    return t0;
  }
  if (props.kind === "orb") {
    const o = props.orb;
    const name_4 = o.name;
    if (!isEnglish) {
      const t0 = `${name_4}`;
      let t1;
      if ($[71] !== t0) {
        t1 = [t0];
        $[71] = t0;
        $[72] = t1;
      } else {
        t1 = $[72];
      }
      let t2;
      if ($[73] !== props.lead || $[74] !== t1) {
        t2 = <Prose lead={props.lead} sentences={t1} />;
        $[73] = props.lead;
        $[74] = t1;
        $[75] = t2;
      } else {
        t2 = $[75];
      }
      return t2;
    }
    const cards = o.channeled_by_cards?.length || 0;
    const relics_0 = o.channeled_by_relics?.length || 0;
    let sentences_4;
    if ($[76] !== cards || $[77] !== name_4 || $[78] !== relics_0) {
      sentences_4 = [];
      sentences_4.push(`${name_4} is an orb in Slay the Spire 2. Orbs fill your orb slots and trigger passively each turn, or all at once when evoked.`);
      if (cards || relics_0) {
        const src = [];
        if (cards) {
          src.push(`${cards} card${cards === 1 ? "" : "s"}`);
        }
        if (relics_0) {
          src.push(`${relics_0} relic${relics_0 === 1 ? "" : "s"}`);
        }
        sentences_4.push(`${name_4} is channeled by ${listWords(src)} in the game.`);
      }
      $[76] = cards;
      $[77] = name_4;
      $[78] = relics_0;
      $[79] = sentences_4;
    } else {
      sentences_4 = $[79];
    }
    let t0;
    if ($[80] !== props.lead || $[81] !== sentences_4) {
      t0 = <Prose lead={props.lead} sentences={sentences_4} />;
      $[80] = props.lead;
      $[81] = sentences_4;
      $[82] = t0;
    } else {
      t0 = $[82];
    }
    return t0;
  }
  if (props.kind === "event") {
    const ev = props.event;
    const name_5 = ev.name;
    if (!isEnglish) {
      const t0 = `${name_5}${ev.act ? ` · ${ev.act}` : ""}`;
      let t1;
      if ($[83] !== t0) {
        t1 = [t0];
        $[83] = t0;
        $[84] = t1;
      } else {
        t1 = $[84];
      }
      let t2;
      if ($[85] !== props.lead || $[86] !== t1) {
        t2 = <Prose lead={props.lead} sentences={t1} />;
        $[85] = props.lead;
        $[86] = t1;
        $[87] = t2;
      } else {
        t2 = $[87];
      }
      return t2;
    }
    let sentences_5;
    if ($[88] !== ev.act || $[89] !== ev.options?.length || $[90] !== ev.relics || $[91] !== ev.type || $[92] !== name_5) {
      sentences_5 = [];
      let t0;
      if ($[94] !== ev.type) {
        t0 = ev.type && ev.type.toLowerCase() !== "event" ? `${ev.type.toLowerCase()} event` : "event";
        $[94] = ev.type;
        $[95] = t0;
      } else {
        t0 = $[95];
      }
      const typeWord = t0;
      let t1;
      if ($[96] !== typeWord) {
        t1 = article(typeWord);
        $[96] = typeWord;
        $[97] = t1;
      } else {
        t1 = $[97];
      }
      sentences_5.push(`${name_5} is ${t1} ${typeWord} in Slay the Spire 2${ev.act ? `, encountered in ${ev.act}` : ""}.`);
      const opts = ev.options?.length || 0;
      if (opts) {
        sentences_5.push(`It presents ${opts} choice${opts === 1 ? "" : "s"}, each leading to a different outcome.`);
      }
      if (ev.relics && ev.relics.length) {
        let t2;
        if ($[98] !== ev.relics) {
          t2 = listWords(ev.relics.map(titleCaseId));
          $[98] = ev.relics;
          $[99] = t2;
        } else {
          t2 = $[99];
        }
        sentences_5.push(`It can reward the ${t2} relic${ev.relics.length > 1 ? "s" : ""}.`);
      }
      $[88] = ev.act;
      $[89] = ev.options?.length;
      $[90] = ev.relics;
      $[91] = ev.type;
      $[92] = name_5;
      $[93] = sentences_5;
    } else {
      sentences_5 = $[93];
    }
    let t0;
    if ($[100] !== props.lead || $[101] !== sentences_5) {
      t0 = <Prose lead={props.lead} sentences={sentences_5} />;
      $[100] = props.lead;
      $[101] = sentences_5;
      $[102] = t0;
    } else {
      t0 = $[102];
    }
    return t0;
  }
  if (props.kind === "encounter") {
    const en = props.encounter;
    const name_6 = en.name;
    if (!isEnglish) {
      const t0 = `${name_6}${en.room_type ? ` · ${en.room_type}` : ""}`;
      let t1;
      if ($[103] !== t0) {
        t1 = [t0];
        $[103] = t0;
        $[104] = t1;
      } else {
        t1 = $[104];
      }
      let t2;
      if ($[105] !== props.lead || $[106] !== t1) {
        t2 = <Prose lead={props.lead} sentences={t1} />;
        $[105] = props.lead;
        $[106] = t1;
        $[107] = t2;
      } else {
        t2 = $[107];
      }
      return t2;
    }
    const t0 = en.room_type || "combat";
    let s1;
    if ($[108] !== en.act || $[109] !== en.monsters || $[110] !== name_6 || $[111] !== t0) {
      const room = t0.toLowerCase();
      const monsters = (en.monsters || []).map(_temp2);
      s1 = `${name_6} is ${article(room)} ${room} encounter in Slay the Spire 2${en.act ? `, fought in ${en.act}` : ""}`;
      if (monsters.length && !(monsters.length === 1 && monsters[0] === name_6)) {
        s1 = s1 + `, pitting you against ${listWords(monsters)}`;
      }
      $[108] = en.act;
      $[109] = en.monsters;
      $[110] = name_6;
      $[111] = t0;
      $[112] = s1;
    } else {
      s1 = $[112];
    }
    const t1 = s1 + ".";
    let sentences_6;
    if ($[113] !== en.is_weak || $[114] !== t1) {
      sentences_6 = [t1];
      if (en.is_weak) {
        sentences_6.push("It is flagged as one of the weaker fights for its act.");
      }
      $[113] = en.is_weak;
      $[114] = t1;
      $[115] = sentences_6;
    } else {
      sentences_6 = $[115];
    }
    let t2;
    if ($[116] !== props.lead || $[117] !== sentences_6) {
      t2 = <Prose lead={props.lead} sentences={sentences_6} />;
      $[116] = props.lead;
      $[117] = sentences_6;
      $[118] = t2;
    } else {
      t2 = $[118];
    }
    return t2;
  }
  if (props.kind === "keyword") {
    const name_7 = props.keyword.name;
    if (!isEnglish) {
      const t0 = `${name_7}`;
      let t1;
      if ($[119] !== t0) {
        t1 = [t0];
        $[119] = t0;
        $[120] = t1;
      } else {
        t1 = $[120];
      }
      let t2;
      if ($[121] !== props.lead || $[122] !== t1) {
        t2 = <Prose lead={props.lead} sentences={t1} />;
        $[121] = props.lead;
        $[122] = t1;
        $[123] = t2;
      } else {
        t2 = $[123];
      }
      return t2;
    }
    const t0 = `${name_7} is a keyword in Slay the Spire 2. Keywords are shared rules text: wherever ${name_7} appears on a card, relic, or power, the same definition applies.`;
    const t1 = `Knowing what ${name_7} does helps you read new cards at a glance and plan around its interactions during a run.`;
    let t2;
    if ($[124] !== t0 || $[125] !== t1) {
      t2 = <Prose sentences={[t0, t1]} />;
      $[124] = t0;
      $[125] = t1;
      $[126] = t2;
    } else {
      t2 = $[126];
    }
    return t2;
  }
  if (props.kind === "intent") {
    const name_8 = props.intent.name;
    if (!isEnglish) {
      const t0 = `${name_8}`;
      let t1;
      if ($[127] !== t0) {
        t1 = [t0];
        $[127] = t0;
        $[128] = t1;
      } else {
        t1 = $[128];
      }
      let t2;
      if ($[129] !== props.lead || $[130] !== t1) {
        t2 = <Prose lead={props.lead} sentences={t1} />;
        $[129] = props.lead;
        $[130] = t1;
        $[131] = t2;
      } else {
        t2 = $[131];
      }
      return t2;
    }
    const t0 = `${name_8} is an enemy intent in Slay the Spire 2. Intents are the icons shown above each enemy that telegraph what it will do on its next turn before you commit to yours.`;
    const t1 = `Reading the ${name_8} intent lets you line up blocks, attacks, and defensive plays around the enemy's plan.`;
    let t2;
    if ($[132] !== t0 || $[133] !== t1) {
      t2 = <Prose sentences={[t0, t1]} />;
      $[132] = t0;
      $[133] = t1;
      $[134] = t2;
    } else {
      t2 = $[134];
    }
    return t2;
  }
  if (props.kind === "modifier") {
    const name_9 = props.modifier.name;
    if (!isEnglish) {
      const t0 = `${name_9}`;
      let t1;
      if ($[135] !== t0) {
        t1 = [t0];
        $[135] = t0;
        $[136] = t1;
      } else {
        t1 = $[136];
      }
      let t2;
      if ($[137] !== props.lead || $[138] !== t1) {
        t2 = <Prose lead={props.lead} sentences={t1} />;
        $[137] = props.lead;
        $[138] = t1;
        $[139] = t2;
      } else {
        t2 = $[139];
      }
      return t2;
    }
    const t0 = `${name_9} is a run modifier in Slay the Spire 2. Modifiers adjust a run's starting conditions or rules so you can tailor the challenge before the run begins.`;
    let t1;
    if ($[140] !== t0) {
      t1 = <Prose sentences={[t0, "They are one of the ways Slay the Spire 2 lets you customize difficulty beyond the Ascension ladder."]} />;
      $[140] = t0;
      $[141] = t1;
    } else {
      t1 = $[141];
    }
    return t1;
  }
  if (props.kind === "affliction") {
    const af = props.affliction;
    const name_10 = af.name;
    if (!isEnglish) {
      const t0 = `${name_10}`;
      let t1;
      if ($[142] !== t0) {
        t1 = [t0];
        $[142] = t0;
        $[143] = t1;
      } else {
        t1 = $[143];
      }
      let t2;
      if ($[144] !== props.lead || $[145] !== t1) {
        t2 = <Prose lead={props.lead} sentences={t1} />;
        $[144] = props.lead;
        $[145] = t1;
        $[146] = t2;
      } else {
        t2 = $[146];
      }
      return t2;
    }
    let sentences_7;
    if ($[147] !== af.is_stackable || $[148] !== name_10) {
      sentences_7 = [];
      sentences_7.push(`${name_10} is an affliction in Slay the Spire 2, a lasting negative effect that follows your character rather than a single card or combat.`);
      sentences_7.push(af.is_stackable ? `${name_10} can stack, so repeated sources make it progressively worse.` : `${name_10} does not stack; picking it up again has no added effect.`);
      $[147] = af.is_stackable;
      $[148] = name_10;
      $[149] = sentences_7;
    } else {
      sentences_7 = $[149];
    }
    let t0;
    if ($[150] !== props.lead || $[151] !== sentences_7) {
      t0 = <Prose lead={props.lead} sentences={sentences_7} />;
      $[150] = props.lead;
      $[151] = sentences_7;
      $[152] = t0;
    } else {
      t0 = $[152];
    }
    return t0;
  }
  if (props.kind === "achievement") {
    const name_11 = props.achievement.name;
    if (!isEnglish) {
      const t0 = `${name_11}`;
      let t1;
      if ($[153] !== t0) {
        t1 = [t0];
        $[153] = t0;
        $[154] = t1;
      } else {
        t1 = $[154];
      }
      let t2;
      if ($[155] !== props.lead || $[156] !== t1) {
        t2 = <Prose lead={props.lead} sentences={t1} />;
        $[155] = props.lead;
        $[156] = t1;
        $[157] = t2;
      } else {
        t2 = $[157];
      }
      return t2;
    }
    const t0 = `${name_11} is an achievement in Slay the Spire 2, unlocked by meeting a specific in-game condition during play.`;
    let t1;
    if ($[158] !== t0) {
      t1 = <Prose sentences={[t0, "Achievements track long-term goals across runs and appear on your Steam profile once earned."]} />;
      $[158] = t0;
      $[159] = t1;
    } else {
      t1 = $[159];
    }
    return t1;
  }
  if (props.kind === "act") {
    const ac = props.act;
    const name_12 = ac.name;
    if (!isEnglish) {
      const t0 = `${name_12}`;
      let t1;
      if ($[160] !== t0) {
        t1 = [t0];
        $[160] = t0;
        $[161] = t1;
      } else {
        t1 = $[161];
      }
      let t2;
      if ($[162] !== props.lead || $[163] !== t1) {
        t2 = <Prose lead={props.lead} sentences={t1} />;
        $[162] = props.lead;
        $[163] = t1;
        $[164] = t2;
      } else {
        t2 = $[164];
      }
      return t2;
    }
    let sentences_8;
    if ($[165] !== ac.bosses || $[166] !== ac.encounters || $[167] !== ac.events || $[168] !== ac.num_rooms || $[169] !== name_12) {
      sentences_8 = [];
      let s1_0 = `${name_12} is an act in Slay the Spire 2`;
      if (ac.num_rooms) {
        s1_0 = s1_0 + `, spanning around ${ac.num_rooms} rooms from entrance to boss`;
      }
      sentences_8.push(s1_0 + ".");
      const counts = [];
      if (ac.bosses?.length) {
        counts.push(`${ac.bosses.length} boss${ac.bosses.length === 1 ? "" : "es"}`);
      }
      if (ac.encounters?.length) {
        counts.push(`${ac.encounters.length} combat encounter${ac.encounters.length === 1 ? "" : "s"}`);
      }
      if (ac.events?.length) {
        counts.push(`${ac.events.length} event${ac.events.length === 1 ? "" : "s"}`);
      }
      if (counts.length) {
        sentences_8.push(`It includes ${listWords(counts)}.`);
      }
      $[165] = ac.bosses;
      $[166] = ac.encounters;
      $[167] = ac.events;
      $[168] = ac.num_rooms;
      $[169] = name_12;
      $[170] = sentences_8;
    } else {
      sentences_8 = $[170];
    }
    let t0;
    if ($[171] !== props.lead || $[172] !== sentences_8) {
      t0 = <Prose lead={props.lead} sentences={sentences_8} />;
      $[171] = props.lead;
      $[172] = sentences_8;
      $[173] = t0;
    } else {
      t0 = $[173];
    }
    return t0;
  }
  if (props.kind === "ascension") {
    const as = props.ascension;
    const name_13 = as.name;
    if (!isEnglish) {
      const t0 = `${name_13}`;
      let t1;
      if ($[174] !== t0) {
        t1 = [t0];
        $[174] = t0;
        $[175] = t1;
      } else {
        t1 = $[175];
      }
      let t2;
      if ($[176] !== props.lead || $[177] !== t1) {
        t2 = <Prose lead={props.lead} sentences={t1} />;
        $[176] = props.lead;
        $[177] = t1;
        $[178] = t2;
      } else {
        t2 = $[178];
      }
      return t2;
    }
    let t0;
    if ($[179] !== as.level || $[180] !== name_13) {
      t0 = as.level > 0 ? [`${name_13} is Ascension ${as.level} in Slay the Spire 2, one rung on the stacking difficulty ladder that layers a new permanent handicap on top of every level below it.`, `You unlock it by winning at Ascension ${as.level - 1}, and each level makes the run tougher than the last.`] : [`${name_13} is the base difficulty in Slay the Spire 2, played without any Ascension modifiers.`, "Clearing a run here unlocks Ascension 1, the first of the stacking difficulty levels."];
      $[179] = as.level;
      $[180] = name_13;
      $[181] = t0;
    } else {
      t0 = $[181];
    }
    const sentences_9 = t0;
    let t1;
    if ($[182] !== props.lead || $[183] !== sentences_9) {
      t1 = <Prose lead={props.lead} sentences={sentences_9} />;
      $[182] = props.lead;
      $[183] = sentences_9;
      $[184] = t1;
    } else {
      t1 = $[184];
    }
    return t1;
  }
  if (props.kind === "monster") {
    const m = props.monster;
    const name_14 = m.name;
    const type = m.type || "enemy";
    const hp = m.min_hp ? `${m.min_hp}${m.max_hp && m.max_hp !== m.min_hp ? `–${m.max_hp}` : ""}` : null;
    const hpAsc = m.min_hp_ascension ? `${m.min_hp_ascension}${m.max_hp_ascension && m.max_hp_ascension !== m.min_hp_ascension ? `–${m.max_hp_ascension}` : ""}` : null;
    if (!isEnglish) {
      const t0 = `${name_14} · ${type}${hp ? ` · ${hp} HP` : ""}`;
      let t1;
      if ($[185] !== t0) {
        t1 = <Prose lead={true} sentences={[t0]} />;
        $[185] = t0;
        $[186] = t1;
      } else {
        t1 = $[186];
      }
      return t1;
    }
    let t0;
    if ($[187] !== hp || $[188] !== hpAsc || $[189] !== m.attack_pattern || $[190] !== m.innate_powers || $[191] !== m.moves || $[192] !== name_14 || $[193] !== props.deadliest || $[194] !== type) {
      const moves = m.moves || [];
      const sentences_10 = [];
      let t1;
      if ($[196] !== type) {
        t1 = article(type);
        $[196] = type;
        $[197] = t1;
      } else {
        t1 = $[197];
      }
      let t2;
      if ($[198] !== type) {
        t2 = type.toLowerCase();
        $[198] = type;
        $[199] = t2;
      } else {
        t2 = $[199];
      }
      let s1_1 = `${name_14} is ${t1} ${t2} enemy in Slay the Spire 2`;
      if (hp) {
        s1_1 = s1_1 + `, entering combat with ${hp} HP`;
      }
      if (hpAsc && hpAsc !== hp) {
        s1_1 = s1_1 + ` (${hpAsc} on higher Ascensions)`;
      }
      sentences_10.push(s1_1 + ".");
      const pat = m.attack_pattern;
      const summary = pat && pat.type !== "cycle" ? randomPatternSummary(pat) : null;
      if (summary) {
        const plain = _temp3;
        const nameOf = mid => moves.find(mv => mv.id === mid)?.name || mid;
        sentences_10.push(randomPatternSentences(summary, nameOf, plain, "eng").join(" "));
      } else {
        if (pat && pat.description) {
          const desc = pat.description;
          if (desc.includes("\u2192")) {
            sentences_10.push(pat.type === "cycle" ? `It cycles through ${desc}.` : `Its attack pattern runs ${desc}.`);
          } else {
            let t3;
            if ($[200] !== desc) {
              t3 = desc.endsWith(".") ? desc : desc + ".";
              $[200] = desc;
              $[201] = t3;
            } else {
              t3 = $[201];
            }
            sentences_10.push(t3);
          }
        } else {
          if (moves.length) {
            sentences_10.push(`It has ${moves.length} known move${moves.length === 1 ? "" : "s"}.`);
          }
        }
      }
      const hardest = [...moves].filter(_temp4).sort(_temp5)[0];
      if (hardest && hardest.damage) {
        const d = hardest.damage;
        const dmg = d.hit_count && d.hit_count > 1 ? `${d.normal}×${d.hit_count} (${d.normal * d.hit_count} total)` : `${d.normal}`;
        let s3 = `Its heaviest attack, ${hardest.name}, deals ${dmg} damage`;
        if (hardest.block != null) {
          s3 = s3 + `, and it gains ${hardest.block} Block on the same turn`;
        }
        sentences_10.push(s3 + ".");
      }
      if (m.innate_powers && m.innate_powers.length) {
        const names = m.innate_powers.map(_temp6);
        sentences_10.push(`It opens the fight already holding ${listWords(names)}.`);
      }
      if (props.deadliest && props.deadliest.killRate > 0) {
        const t3 = props.deadliest.name;
        let t4;
        if ($[202] !== props.deadliest.killRate) {
          t4 = props.deadliest.killRate.toFixed(1);
          $[202] = props.deadliest.killRate;
          $[203] = t4;
        } else {
          t4 = $[203];
        }
        sentences_10.push(`Across community-tracked runs, the ${t3} fight proves fatal to ${t4}% of the parties that reach it.`);
      }
      t0 = <Prose lead={true} sentences={sentences_10} />;
      $[187] = hp;
      $[188] = hpAsc;
      $[189] = m.attack_pattern;
      $[190] = m.innate_powers;
      $[191] = m.moves;
      $[192] = name_14;
      $[193] = props.deadliest;
      $[194] = type;
      $[195] = t0;
    } else {
      t0 = $[195];
    }
    return t0;
  }
  const pw = props.power;
  const name_15 = pw.name;
  const type_0 = pw.type || "Buff";
  const stack = pw.stack_type || "Counter";
  if (!isEnglish) {
    const t0 = `${name_15} · ${type_0} · ${stack}`;
    let t1;
    if ($[204] !== t0) {
      t1 = [t0];
      $[204] = t0;
      $[205] = t1;
    } else {
      t1 = $[205];
    }
    let t2;
    if ($[206] !== props.lead || $[207] !== t1) {
      t2 = <Prose lead={props.lead} sentences={t1} />;
      $[206] = props.lead;
      $[207] = t1;
      $[208] = t2;
    } else {
      t2 = $[208];
    }
    return t2;
  }
  let sentences_11;
  if ($[209] !== name_15 || $[210] !== props.appliedByCount || $[211] !== stack || $[212] !== type_0) {
    sentences_11 = [];
    let t0;
    if ($[214] !== type_0) {
      t0 = type_0.toLowerCase();
      $[214] = type_0;
      $[215] = t0;
    } else {
      t0 = $[215];
    }
    sentences_11.push(`${name_15} is a ${t0} power that stacks as ${stack}.`);
    if (type_0 === "Buff") {
      sentences_11.push(`Buffs are positive effects on the recipient, applying ${name_15} to a player or ally improves their position; applying it to an enemy strengthens that enemy.`);
    } else {
      if (type_0 === "Debuff") {
        sentences_11.push(`Debuffs are negative effects on the recipient, applying ${name_15} to an enemy weakens them; applying it to a player or ally is a drawback.`);
      } else {
        sentences_11.push(`${type_0} powers are persistent state attached to a creature for the duration specified by their stacks.`);
      }
    }
    if (props.appliedByCount > 0) {
      sentences_11.push(`${name_15} is applied by ${props.appliedByCount} card${props.appliedByCount === 1 ? "" : "s"} in the game (listed below). It can also be applied by relics, potions, or enemy moves depending on context.`);
    } else {
      sentences_11.push(`${name_15} is not directly applied by any cards in the player's pool, it appears via enemy moves, relics, or events.`);
    }
    $[209] = name_15;
    $[210] = props.appliedByCount;
    $[211] = stack;
    $[212] = type_0;
    $[213] = sentences_11;
  } else {
    sentences_11 = $[213];
  }
  let t0;
  if ($[216] !== props.lead || $[217] !== sentences_11) {
    t0 = <Prose lead={props.lead} sentences={sentences_11} />;
    $[216] = props.lead;
    $[217] = sentences_11;
    $[218] = t0;
  } else {
    t0 = $[218];
  }
  return t0;
}
function _temp6(p_1) {
  return titleCaseId(p_1.power_id);
}
function _temp5(a, b) {
  return b.damage.normal * (b.damage.hit_count || 1) - a.damage.normal * (a.damage.hit_count || 1);
}
function _temp4(mv_0) {
  return mv_0.damage && mv_0.damage.normal != null;
}
function _temp3(key, values) {
  return key.replace(/\{(\w+)\}/g, (_, k) => String(values?.[k] ?? ""));
}
function _temp2(mm) {
  return mm.name;
}
function _temp(p_0) {
  return `${p_0.amount} ${p_0.power}`;
}
function Prose(t0) {
  const $ = _c(8);
  const {
    sentences,
    lead
  } = t0;
  if (lead) {
    let t1;
    if ($[0] !== sentences) {
      t1 = sentences.map(_temp7);
      $[0] = sentences;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <section className="entity-lead">{t1}</section>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== sentences) {
    t1 = sentences.map(_temp8);
    $[4] = sentences;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t1) {
    t2 = <section className="mt-6 pt-5 border-t border-[var(--border-subtle)] text-sm leading-relaxed text-[var(--text-secondary)] space-y-2">{t1}</section>;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function _temp8(s_0, i_0) {
  return <p key={i_0}>{s_0}</p>;
}
function _temp7(s, i) {
  return <p key={i}>{s}</p>;
}